import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { PredictionPositionSummary } from '@/feature/betslip-sbk/utils/prediction-position';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useOddsFormat } from '@/hooks/use-odds-format';
import { formatOdds } from '@/utils/format-odds';

import { ds } from './lib/design.system';

export type PositionSummaryProps = PredictionPositionSummary & {
    odds?: number;
    variant?: 'estimated' | 'actual';
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
    <Row justifyContent="space-between" alignItems="center" py="s16">
        <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
            {label}
        </Text>
        <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
            {value}
        </Text>
    </Row>
);

export const PositionSummary = ({
    odds,
    contracts,
    fees,
    cost,
    payout,
    variant = 'estimated',
}: PositionSummaryProps) => {
    const { t } = useTranslation(['betslip_sbk', 'common', 'bets']);
    const { formatCurrency } = useFormatCurrency();
    const oddsFormat = useOddsFormat();

    return (
        <Box>
            <Text variant="headlineMedium" lineHeight={28} color={ds.colorToken.text.primary} textAlign="center">
                {t('position_summary')}
            </Text>
            <Box mt="s24">
                <SummaryRow label={t('common:price')} value={odds ? formatOdds(oddsFormat, odds) : '-'} />
                <LineSeparator />
                <SummaryRow
                    label={t('contracts')}
                    value={contracts.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                />
                <LineSeparator />
                <SummaryRow
                    label={variant === 'estimated' ? t('estimated_fees') : t('fees')}
                    value={formatCurrency(fees)}
                />
                <LineSeparator />
                <SummaryRow
                    label={variant === 'estimated' ? t('estimated_cost') : t('bets:cost')}
                    value={formatCurrency(cost)}
                />
                <LineSeparator />
                <SummaryRow label={t('common:payout')} value={formatCurrency(payout)} />
            </Box>
        </Box>
    );
};
